import React from 'react';
import { Box, Button, ButtonBase, CircularProgress, IconButton, MenuItem, Select, Skeleton, Tooltip, Typography } from '@mui/material';
import { Add, AutoAwesome, KeyboardArrowDown, ViewWeekOutlined } from '@mui/icons-material';
import { useDealFlowStatusCounts, useDealFlowStatusRows } from 'api/dealFlowList';
import AsyncTableListing from 'ui-components/AsyncTableListing';
import StatusPill, { listStatusMeta } from 'views/deal_flow_board/components/StatusPill';
import StatusSegmentedControl from './StatusSegmentedControl';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CountPlaceholder, TweenedNumber } from './useTweenedNumber';

// Grouped Deal Flow list (ClickUp-style): every status as a collapsible group. A group's rows are fetched
// only once it is opened, then served from cache; the counts for all groups come from one call.
// Rows per load: the old table's page sizes, saved in the same "limit" cookie.
export const PAGE_SIZE_OPTIONS = [10, 20, 25, 30, 40, 50];
const EMPTY_SET = new Set();
const COUNT_SX = { fontSize: '0.8125rem', fontWeight: 500, color: '#475467' };

// Open groups persist per investor; first visit opens only the first status.
const openKey = (investorId) => `dealflow_list_open_${investorId}`;
const readOpen = (investorId) => {
    try {
        const saved = JSON.parse(window.localStorage.getItem(openKey(investorId)) || 'null');
        return Array.isArray(saved) ? new Set(saved) : null;
    } catch {
        return null;
    }
};

// Compact rows on the shared table (designs A and B): white header, 12px labels, hairline rows, hover tint.
export const compactRowsSx = {
    // Labels 13.25px (0.75rem + 2px on the 15px root); the header checkbox keeps the rows' size and colour.
    '&& thead th': {
        backgroundColor: '#FFFFFF', color: '#667085', fontSize: 'calc(0.75rem + 2px)', fontWeight: 500,
        padding: '6px 12px', borderBottom: '1px solid #EAECF0',
    },
    '&& thead th *': { fontSize: 'calc(0.75rem + 2px)', fontWeight: 500, color: '#667085' },
    '&& thead th .MuiCheckbox-root, && thead th .MuiCheckbox-root *': { fontSize: '1.5rem', color: '#000000' },
    '&& tbody td': { padding: '6px 12px', borderBottom: '1px solid #F2F4F7' },
    '&& tbody tr:hover td': { backgroundColor: '#F9FAFB' },
};

// Motion: exits faster than entries; only opacity/height animate. Reduced-motion users get none of it.
const EASE_OUT = 'cubic-bezier(0.05, 0.7, 0.1, 1)';
const EASE_IN = 'cubic-bezier(0.3, 0, 0.8, 0.15)';
const motionSx = {
    '@keyframes dfRowIn': { from: { opacity: 0 }, to: { opacity: 1 } },
    '@keyframes dfCellOut': { to: { opacity: 0, paddingTop: 0, paddingBottom: 0 } },
    '@keyframes dfContentOut': { from: { maxHeight: '120px' }, to: { maxHeight: 0, opacity: 0 } },
    // Rows that join a group later (Show more, a new search's results) fade in.
    '& tbody tr.row-enter': { animation: `dfRowIn 200ms ${EASE_OUT} both` },
    // A row leaving for another status fades and folds away (~200ms).
    '& tbody tr.row-leaving td': { animation: `dfCellOut 200ms ${EASE_IN} forwards`, overflow: 'hidden' },
    '& tbody tr.row-leaving td > *': { animation: `dfContentOut 200ms ${EASE_IN} forwards`, overflow: 'hidden' },
    '@media (prefers-reduced-motion: reduce)': { '& tbody tr, & tbody tr td, & tbody tr td > *': { animation: 'none !important' } },
};
// Every time a table appears (fresh or cached) it fades in as one piece, ~200ms: checkbox, cells, status and actions
// arrive together. No stagger: for dense, often-opened tables NN/g, Primer and Atlassian all advise one quick reveal.
const tableInSx = {
    '@keyframes dfTableIn': { from: { opacity: 0 }, to: { opacity: 1 } },
    '& table': { animation: 'dfTableIn 200ms cubic-bezier(0, 0, 0, 1) backwards' },
    '@media (prefers-reduced-motion: reduce)': { '& table': { animation: 'none' } },
};

// Placeholder shaped like the real rows (checkbox, avatar, name + email, a few columns), so the swap is calm.
const SkeletonRows = ({ rows = 4 }) => (
    <Box aria-hidden>
        {Array.from({ length: rows }, (_, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '16px', height: 52, px: '12px', borderBottom: '1px solid #F2F4F7' }}>
                <Skeleton animation="wave" variant="rounded" width={18} height={18} />
                <Skeleton animation="wave" variant="rounded" width={38} height={38} sx={{ borderRadius: '9px' }} />
                <Box sx={{ width: 220 }}>
                    <Skeleton animation="wave" variant="text" width={150 - (i % 3) * 20} height={18} />
                    <Skeleton animation="wave" variant="text" width={180} height={14} />
                </Box>
                {[110, 70, 120, 90].map((w, j) => <Skeleton key={j} animation="wave" variant="text" width={w} height={16} sx={{ ml: '24px' }} />)}
            </Box>
        ))}
    </Box>
);

// Sticky layers in the one list scroller: rail at the top, then the group header, then the column header.
// 60px: the status strip (40px) gets 10px of air above and below.
const RAIL_HEIGHT = 60;
const GROUP_HEADER_HEIGHT = 48;
// Things that span the visible width (rail, group header, notes, buttons) stay put while the list scrolls sideways.
const PIN_LEFT = { position: 'sticky', left: 0, width: 'var(--list-vw, 100%)', boxSizing: 'border-box' };

// Inside a group the table is borderless and grows with its rows. It has no scroll box of its own, so the list
// scroller moves it: the column header sticks under the group header and every group scrolls sideways together.
const tableSx = {
    border: 'none !important', borderRadius: 0, boxShadow: 'none !important', maxHeight: 'none !important', height: 'auto !important',
    overflow: 'visible !important',
    '& thead': { top: `${RAIL_HEIGHT + GROUP_HEADER_HEIGHT}px !important` },
    ...compactRowsSx,
};

// Column header row height under the group header; the status glow spans both.
const COLUMN_HEADER_HEIGHT = 33;

// Soft, uneven glow in the status's colour family behind the group header and its column header (like a light
// bleeding in, not a flat fill): blobs of different strength and size, one nudged to a neighbouring hue.
// Translucent, so header text stays readable; it sits in the pinned header, so it does not slide sideways.
const StatusGlow = ({ color, height, pulseKey }) => {
    const tint = (pct) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;
    const layers = [
        `radial-gradient(38% 150% at 6% 0%, ${tint(30)} 0%, transparent 70%)`,
        `radial-gradient(30% 120% at 34% 100%, ${tint(16)} 0%, transparent 72%)`,
        `radial-gradient(42% 170% at 68% 20%, ${tint(12)} 0%, transparent 70%)`,
        `radial-gradient(26% 110% at 96% 90%, ${tint(18)} 0%, transparent 70%)`,
    ];
    return (
        <Box
            aria-hidden
            className="status-glow"
            sx={{
                position: 'absolute', left: 0, right: 0, top: 0, height, zIndex: 0, pointerEvents: 'none',
                background: layers.join(', '),
                // A neighbouring hue for one blob where the browser supports relative colours.
                '@supports (color: oklch(from red l c h))': {
                    background: [
                        layers[0],
                        `radial-gradient(30% 120% at 34% 100%, oklch(from ${color} calc(l + 0.06) c calc(h + 28) / 0.16) 0%, transparent 72%)`,
                        layers[2],
                        layers[3],
                    ].join(', '),
                },
                transition: 'height 0.15s ease',
            }}
        >
            {/* One brief brighten when deals land in this status (re-keyed per move). */}
            {pulseKey && (
                <Box
                    key={pulseKey}
                    sx={{
                        position: 'absolute', inset: 0,
                        background: `linear-gradient(90deg, color-mix(in srgb, ${color} 26%, transparent), color-mix(in srgb, ${color} 10%, transparent) 70%, transparent)`,
                        '@keyframes dfPulse': { '0%': { opacity: 0 }, '30%': { opacity: 1 }, '100%': { opacity: 0 } },
                        animation: `dfPulse 700ms ${EASE_OUT} both`,
                        '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
                    }}
                />
            )}
        </Box>
    );
};

// Every group renders its own table, so columns get one fixed width each: identical in every group, they line up.
// Widths start from the column's minWidth, widened where content needs room.
const COLUMN_WIDTHS = { checkbox: 48, name: 300, location: 150, category_sub_sector: 180, pipeline_status_value: 160, pipelineUpdatedDate: 120, links: 130 };
const columnLayoutSx = (columns = [], hidden = []) => {
    const visible = columns.filter((col) => !hidden.includes(col.id || col.accessor));
    const widths = visible.map((col) => COLUMN_WIDTHS[col.accessor] ?? Math.max(Number(col.minWidth) || 0, 110));
    const total = widths.reduce((sum, w) => sum + w, 0);
    const hasCheckbox = (visible[0]?.id || visible[0]?.accessor) === 'checkbox';
    return widths.reduce((sx, w, i) => ({
        ...sx,
        [`& thead th:nth-of-type(${i + 1})`]: { width: `${w}px`, maxWidth: `${w}px` },
    }), {
        '& table': { tableLayout: 'fixed', width: `${total}px !important`, minWidth: '100%' },
        '& tbody td': { overflow: 'hidden' },
        // Checkbox and Name both stay while scrolling sideways (the table pins Name at 0, over the checkbox).
        ...(hasCheckbox ? {
            '& tbody tr > td:first-of-type': { position: 'sticky', left: 0, zIndex: 1 },
            '& thead th:nth-of-type(2), & tbody tr > td:nth-of-type(2)': { left: `${widths[0]}px !important` },
        } : {}),
    });
};

// Sticky rail of every status (icon, name, count): click jumps to the group, scrolling highlights the one in view.
// railStart / railEnd: the page's own controls (pipeline switcher; search, filter, view) placed in the rail.
// The board uses it with no statuses (its columns are the statuses), so List and Board share one header bar.
export const StatusRail = ({ statuses = [], counts, activeId, onJump, onOpenFields, onAddStatus, canAddStatus, railStart, railEnd, railTrailing, sx }) => (
    <Box
        component="nav"
        aria-label={statuses.length ? 'Deal statuses' : 'Deal flow controls'}
        sx={{
            ...PIN_LEFT, top: 0, zIndex: 1200, height: RAIL_HEIGHT,
            display: 'flex', alignItems: 'center', px: '12px',
            bgcolor: 'rgba(255, 255, 255, 0.92)', backdropFilter: 'blur(8px)', borderBottom: '1px solid #EAECF0',
            // Phones: the rail scrolls sideways as one row, so the statuses keep their width.
            overflowX: { xs: 'auto', sm: 'visible' }, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
            ...sx,
        }}
    >
        {railStart && (
            <>
                {railStart}
                {statuses.length > 0 && <Box aria-hidden sx={{ flexShrink: 0, width: '1px', height: 20, bgcolor: '#EAECF0', mx: '10px' }} />}
            </>
        )}
        <Box sx={{ minWidth: { xs: 'auto', sm: 0 }, flex: statuses.length ? { xs: '0 0 auto', sm: 1 } : 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
            {statuses.length > 0 && (
                <Box sx={{ minWidth: { xs: 'auto', sm: 0 } }}>
                    <StatusSegmentedControl statuses={statuses} counts={counts} activeId={activeId} onSelect={onJump} mode="nav" ariaLabel="Jump to status" />
                </Box>
            )}
            {/* Where the old table's "+ Add" sat: at the end of the statuses. Disabled (not hidden) at the limit, saying why. */}
            {onAddStatus && (
                <Tooltip title={canAddStatus ? 'Add status' : 'You can have up to 8 statuses. Manage statuses to change them.'}>
                    <span>
                        <IconButton
                            onClick={onAddStatus}
                            disabled={!canAddStatus}
                            aria-label="Add status"
                            sx={{
                                flexShrink: 0, width: 32, height: 32, color: '#475467',
                                border: '1px dashed #D0D5DD', bgcolor: '#FFFFFF',
                                '&:hover': { bgcolor: '#F9FAFB', color: '#3538CD', borderColor: '#A4BCFD' },
                                '&.Mui-focusVisible': { outline: '2px solid #3538CD', outlineOffset: '1px' },
                                '&.Mui-disabled': { color: '#D0D5DD', borderColor: '#EAECF0' },
                            }}
                        >
                            <Add sx={{ fontSize: 18 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </Box>
        {railEnd && <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', ml: '12px', flexShrink: 0 }}>{railEnd}</Box>}
        {onOpenFields && (
            <ButtonBase
                onClick={onOpenFields}
                aria-label="Show or hide fields"
                sx={{
                    flexShrink: 0, ml: railEnd ? '8px' : '12px', height: 32, px: '10px', gap: '6px', borderRadius: '8px',
                    fontSize: '0.8125rem', fontWeight: 600, color: '#344054', border: '1px solid #EAECF0', bgcolor: '#FFFFFF',
                    '&:hover': { bgcolor: '#F9FAFB' },
                    '&.Mui-focusVisible': { outline: '2px solid #3538CD', outlineOffset: '1px' },
                }}
            >
                <ViewWeekOutlined sx={{ fontSize: 18 }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Fields</Box>
            </ButtonBase>
        )}
        {railTrailing && <Box sx={{ display: 'flex', alignItems: 'center', ml: '8px', flexShrink: 0 }}>{railTrailing}</Box>}
    </Box>
);

// Shown on the New group while AI shortlisting runs (manual AI Shortlist or Auto-Shortlist); the snackbar keeps
// the per-startup progress. Same shimmer as the bulk bar's AI Shortlist button.
const AiWorkingChip = () => (
    <Box
        role="status"
        sx={{
            display: 'inline-flex', alignItems: 'center', gap: '5px', height: 24, px: '9px', borderRadius: '999px',
            fontSize: '0.75rem', fontWeight: 600, color: '#5925DC',
            background: 'linear-gradient(135deg, rgba(120,82,248,0.14), rgba(132,174,246,0.14))',
            border: '1px solid rgba(120,82,248,0.25)',
            position: 'relative', overflow: 'hidden',
            '& svg': { fontSize: 14 },
            '&::after': {
                content: '""', position: 'absolute', top: 0, bottom: 0, left: 0, width: '50%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)',
                transform: 'translateX(-100%)', animation: 'dfAiShimmer 1.8s ease-in-out infinite', pointerEvents: 'none',
            },
            '@keyframes dfAiShimmer': { '0%': { transform: 'translateX(-100%)' }, '60%, 100%': { transform: 'translateX(220%)' } },
            '@media (prefers-reduced-motion: reduce)': { '&::after': { display: 'none' } },
        }}
    >
        <AutoAwesome /> AI shortlisting…
    </Box>
);

const StatusGroup = ({
    status, investorId, buildRequest, collapsed, count, onToggle, formatRows, tableProps,
    selection, onInvite, sectionRef, leavingKeys, pulseKey, aiRunning, isAdded, entranceDelay, columnsReady = true,
    pageSize, onPageSizeChange, isFiltered,
}) => {
    const reduceMotion = useReducedMotion();
    // Disabled while closed: a closed group never calls the API.
    const query = useDealFlowStatusRows(investorId, status.value, buildRequest, pageSize, { enabled: !collapsed });
    const rows = React.useMemo(
        () => (query.data ? query.data.pages.flatMap((p) => p?.data || []) : null),
        [query.data],
    );
    const pages = query.data?.pages;
    const loadedTotal = pages?.length ? pages[pages.length - 1]?.totalCount : undefined;
    const total = count ?? loadedTotal;
    const { color, Icon } = listStatusMeta(status);
    const isNew = status?.mappedValue === 'invited';

    const data = React.useMemo(() => formatRows(rows || []), [rows, formatRows]);
    // Past 10s without rows, say it is still loading and offer a retry.
    const [slowLoad, setSlowLoad] = React.useState(false);
    React.useEffect(() => {
        if (collapsed || rows !== null) { setSlowLoad(false); return undefined; }
        const timer = setTimeout(() => setSlowLoad(true), 10000);
        return () => clearTimeout(timer);
    }, [collapsed, rows]);
    // The counts call is the fresher source. If it disagrees with this group's loaded total (startups moved by AI
    // shortlisting or a teammate), refetch the group once for that count, so header and rows agree.
    const healedForRef = React.useRef(null);
    React.useEffect(() => {
        if (collapsed || count == null || loadedTotal == null || count === loadedTotal || query.isFetching) return;
        if (healedForRef.current === count) return;
        healedForRef.current = count;
        query.refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collapsed, count, loadedTotal, query.isFetching]);
    const isLeaving = (row) => leavingKeys?.size && (leavingKeys.has(String(row?._id)) || leavingKeys.has(String(row?.pipeline_id)));
    // Rows already on screen; anything new after the first paint (Show more, a new search) gets row-enter once.
    const seenRef = React.useRef(null);
    const rowKey = (row) => String(row?.pipeline_id ?? row?._id);
    if (seenRef.current === null && rows) seenRef.current = new Set(rows.map(rowKey));
    React.useEffect(() => { if (rows && seenRef.current) rows.forEach((row) => seenRef.current.add(rowKey(row))); }, [rows]);
    const getRowClassName = (row) => {
        if (isLeaving(row)) return 'row-leaving';
        return seenRef.current && !seenRef.current.has(rowKey(row)) ? 'row-enter' : '';
    };
    const layoutSx = React.useMemo(
        () => ({
            ...tableSx,
            ...columnLayoutSx(tableProps?.columns, tableProps?.hidden_columns),
            // Borderless: the status glow above the header does the separating.
            '&&& thead th': { borderBottomColor: 'transparent' },
            ...motionSx,
            ...tableInSx,
        }),
        [tableProps?.columns, tableProps?.hidden_columns],
    );
    const showsTable = !collapsed && rows?.length > 0;
    const remaining = Math.max(0, (loadedTotal ?? 0) - (rows?.length || 0));

    return (
        <Box component="section" ref={sectionRef} aria-label={`${status?.label} (${total ?? 0})`} sx={{
            '& + &': { borderTop: '1px solid #EAECF0' },
            // A status added after the list loaded fades in rather than popping.
            ...(isAdded && {
                '@keyframes dfGroupIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                animation: `dfGroupIn 280ms ${EASE_OUT} both`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }),
            // First visit only: the groups settle in one after another, top to bottom.
            ...(!isAdded && entranceDelay != null && {
                '@keyframes dfGroupRise': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'none' } },
                animation: `dfGroupRise 240ms ${EASE_OUT} ${entranceDelay}ms both`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }),
        }}>
            <Box sx={{
                ...PIN_LEFT, top: RAIL_HEIGHT, zIndex: 1100, bgcolor: '#FFFFFF',
                display: 'flex', alignItems: 'center', gap: '8px', height: GROUP_HEADER_HEIGHT, px: '12px',
                '& > :not(.status-glow)': { position: 'relative', zIndex: 1 },
            }}>
                <StatusGlow color={color} height={GROUP_HEADER_HEIGHT + (showsTable ? COLUMN_HEADER_HEIGHT : 0)} pulseKey={pulseKey} />
                <IconButton
                    size="small"
                    onClick={() => onToggle(status.value)}
                    aria-expanded={!collapsed}
                    aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${status?.label}`}
                    sx={{ width: 28, height: 28, color: '#475467' }}
                >
                    <KeyboardArrowDown sx={{ fontSize: 20, transition: 'transform 0.15s ease', transform: collapsed ? 'rotate(-90deg)' : 'none' }} />
                </IconButton>
                <StatusPill color={color} icon={Icon} noIconDot>{status?.label}</StatusPill>
                <Typography component="span" sx={COUNT_SX}>
                    <TweenedNumber value={total} placeholder={<CountPlaceholder />} />
                </Typography>
                {isNew && aiRunning && <AiWorkingChip />}
                {isNew && onInvite && (
                    <Tooltip title="Invite startup">
                        <IconButton size="small" onClick={onInvite} aria-label="Invite startup" sx={{ width: 28, height: 28, color: '#475467' }}>
                            <Add sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* Opening/closing a group folds its body (~200ms); groups already open on load do not animate in. */}
            <AnimatePresence initial={false}>
            {!collapsed && (
                <motion.div
                    key="body"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: reduceMotion ? 0 : 0.2, ease: [0.2, 0, 0, 1] }}
                    // Clipped with clip-path, never overflow:hidden: overflow made this box the sticky column
                    // header's scroller, so when it lingered the header stuck 96px down, under the first row.
                    // A new search/filter keeps the last rows, dimmed, until the new ones land.
                    style={{
                        overflow: 'visible', clipPath: 'inset(0)',
                        transitionProperty: 'filter', transitionDuration: '150ms', filter: query.isPreviousData ? 'opacity(0.5)' : 'none',
                    }}
                >
                {query.isError && rows === null ? (
                    <Box sx={{ ...PIN_LEFT, display: 'flex', alignItems: 'center', gap: '8px', px: '48px', pb: '14px', fontSize: '0.8125rem', color: '#B42318' }}>
                        Could not load {status?.label}.
                        <ButtonBase onClick={() => query.refetch()} sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#3538CD', textDecoration: 'underline' }}>
                            Try again
                        </ButtonBase>
                    </Box>
                ) : rows === null || !columnsReady ? (
                    // Rows wait for the column setting too, so the table never draws with the wrong columns first.
                    // Shown only if loading takes over 200ms, so a quick load never flashes a skeleton; past 10s it says so.
                    <Box sx={{
                        ...PIN_LEFT, pb: '8px',
                        '@keyframes dfFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                        animation: `dfFadeIn 150ms ${EASE_OUT} 200ms both`,
                        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                    }}>
                        <SkeletonRows />
                        {slowLoad && (
                            <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: '48px', pt: '10px', fontSize: '0.8125rem', color: '#667085' }}>
                                Still loading {status?.label}…
                                <ButtonBase onClick={() => query.refetch()} sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#3538CD', textDecoration: 'underline' }}>
                                    Retry
                                </ButtonBase>
                            </Box>
                        )}
                    </Box>
                ) : rows.length === 0 ? (
                    // As the old table: only New invites (startups enter there), and only when nothing is filtered out.
                    isNew && onInvite && !isFiltered ? (
                        <Box sx={{ ...PIN_LEFT, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', px: '48px', pb: '14px' }}>
                            <Typography sx={{ fontSize: '0.8125rem', color: '#667085' }}>
                                Invite startups to start building your deal flow
                            </Typography>
                            <Button size="small" variant="outlined" startIcon={<Add />} onClick={onInvite} sx={{ textTransform: 'none', borderRadius: '8px' }}>
                                Invite Startup
                            </Button>
                        </Box>
                    ) : (
                        <Typography sx={{ ...PIN_LEFT, px: '48px', pb: '14px', fontSize: '0.8125rem', color: '#667085' }}>
                            {isFiltered ? `No startups in ${status?.label} match your search or filters` : `No startups in ${status?.label}`}
                        </Typography>
                    )
                ) : (
                    <>
                        <AsyncTableListing
                            {...tableProps}
                            data={data}
                            count={rows.length}
                            pagination={false}
                            page={1}
                            limit={rows.length}
                            maxHeight="none"
                            ownScroll={false}
                            sx={layoutSx}
                            selectedIds={selection.isActive ? selection.selectedIds : EMPTY_SET}
                            selectAll={selection.isActive ? selection.selectAll : false}
                            setSelectedIds={selection.setSelectedIds}
                            setSelectAll={selection.setSelectAll}
                            getRowClassName={getRowClassName}
                        />
                        {query.hasNextPage && remaining > 0 && (
                            <Box sx={{ ...PIN_LEFT, display: 'flex', alignItems: 'center', gap: '8px', pr: '12px' }}>
                                <ButtonBase
                                    onClick={() => query.fetchNextPage()}
                                    disabled={query.isFetchingNextPage}
                                    sx={{
                                        flex: 1, justifyContent: 'flex-start', gap: '6px', minHeight: 40, px: '48px',
                                        fontSize: '0.8125rem', fontWeight: 500, color: '#475467',
                                        '&:hover': { bgcolor: '#F9FAFB', color: '#3538CD' },
                                        '&.Mui-focusVisible': { outline: '2px solid #3538CD', outlineOffset: '-2px' },
                                    }}
                                >
                                    {query.isFetchingNextPage ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : <KeyboardArrowDown sx={{ fontSize: 18 }} />}
                                    Show more ({remaining} more)
                                </ButtonBase>
                                {onPageSizeChange && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, fontSize: '0.8125rem', color: '#667085' }}>
                                        Load
                                        <Select
                                            size="small"
                                            variant="standard"
                                            disableUnderline
                                            value={pageSize}
                                            onChange={(event) => onPageSizeChange(Number(event.target.value))}
                                            inputProps={{ 'aria-label': 'Startups per load' }}
                                            sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#344054' }}
                                        >
                                            {PAGE_SIZE_OPTIONS.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
                                        </Select>
                                        at a time
                                    </Box>
                                )}
                            </Box>
                        )}
                    </>
                )}
                </motion.div>
            )}
            </AnimatePresence>
        </Box>
    );
};

const GroupedDealList = ({
    statuses = [], investorId, buildRequest, formatRows, tableProps,
    activeStatusId, onActivateStatus, selectedIds, setSelectedIds, selectAll, setSelectAll,
    onCountsChange, onInvite, onAddStatus, canAddStatus = true, onOpenFields, railStart, railEnd, railTrailing, leavingKeys, pulse, aiRunning, columnsReady,
    pageSize = PAGE_SIZE_OPTIONS[0], onPageSizeChange, isFiltered = false,
}) => {
    // Statuses present on first render; any added later animate in.
    const initialStatusIds = React.useRef(null);
    if (initialStatusIds.current === null && statuses.length) initialStatusIds.current = new Set(statuses.map((s) => s.value));
    const [open, setOpen] = React.useState(() => readOpen(investorId));
    const openSet = React.useMemo(() => open ?? new Set(statuses[0] ? [statuses[0].value] : []), [open, statuses]);
    // On arrival (or new statuses) never show every group closed: open the first one.
    const firstStatusId = statuses[0]?.value;
    const statusIdsJoined = statuses.map((s) => s.value).join(',');
    React.useEffect(() => {
        if (!firstStatusId) return;
        setOpen((prev) => {
            const ids = statusIdsJoined.split(',');
            return prev && !ids.some((id) => prev.has(id)) ? new Set([...prev, firstStatusId]) : prev;
        });
    }, [firstStatusId, statusIdsJoined]);
    const collapsedSet = React.useMemo(
        () => new Set(statuses.filter((s) => !openSet.has(s.value)).map((s) => s.value)),
        [openSet, statuses],
    );

    const toggleGroup = (statusId) => {
        const next = new Set(openSet);
        next.has(statusId) ? next.delete(statusId) : next.add(statusId);
        setOpen(next);
        try { window.localStorage.setItem(openKey(investorId), JSON.stringify([...next])); } catch { /* keep in memory */ }
    };

    // Every status count in one request (same filters as the rows).
    const countsQuery = useDealFlowStatusCounts(investorId, buildRequest(null, 1, pageSize));
    // First visit = nothing cached when the list mounted. A cached reload paints at once with no entrance.
    const [firstVisit] = React.useState(() => countsQuery.data == null);
    // The API lists only statuses with matches; a status it leaves out has 0, not "unknown".
    // Keyed on the status ids, not the array: the parent passes a new array every render, and a new counts
    // object each time fed onCountsChange -> parent render -> new array: an endless re-render loop.
    const statusIdsKey = statuses.map((s) => s.value).join(',');
    const counts = React.useMemo(() => (countsQuery.data
        ? Object.fromEntries(statusIdsKey.split(',').filter(Boolean).map((id) => [id, countsQuery.data[id] ?? 0]))
        : undefined), [countsQuery.data, statusIdsKey]);
    React.useEffect(() => { if (counts) onCountsChange?.(counts); }, [counts, onCountsChange]);

    // Rail: the group whose top has reached the rail is current, i.e. the one being read. (An "at the bottom,
    // highlight the last group" rule picked a closed Rejected while Selected filled the screen.)
    const scrollRef = React.useRef(null);
    const sectionEls = React.useRef({});
    const [railActive, setRailActive] = React.useState(statuses[0]?.value ?? null);
    // While a rail click scrolls the list, the groups it passes must not steal the highlight (thumb bounced back).
    const jumpLockRef = React.useRef(null);
    // A clicked status keeps the highlight until the user scrolls, even if it cannot reach the top (last groups).
    const pinnedRef = React.useRef(null);
    const scrollFrameRef = React.useRef(0);
    const releaseTimerRef = React.useRef(0);
    const updateRailFromScroll = () => {
        const box = scrollRef.current;
        if (!box || jumpLockRef.current || pinnedRef.current) return;
        let current = statuses[0]?.value ?? null;
        statuses.forEach((s) => {
            const el = sectionEls.current[s.value];
            if (el && el.offsetTop - box.scrollTop <= RAIL_HEIGHT + 8) current = s.value;
        });
        setRailActive(current);
    };
    // Opening/closing a group or rows arriving moves groups without a scroll event; re-check then too.
    const contentRef = React.useRef(null);
    const updateRef = React.useRef(updateRailFromScroll);
    updateRef.current = updateRailFromScroll;
    React.useEffect(() => {
        if (!contentRef.current || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(scrollFrameRef.current);
            scrollFrameRef.current = requestAnimationFrame(() => updateRef.current());
        });
        observer.observe(contentRef.current);
        return () => observer.disconnect();
    }, []);
    // Visible width of the scroller, for the pinned full-width pieces (they must not grow with the wide tables).
    React.useEffect(() => {
        const box = scrollRef.current;
        if (!box || typeof ResizeObserver === 'undefined') return undefined;
        const setWidth = () => box.style.setProperty('--list-vw', `${box.clientWidth}px`);
        setWidth();
        const observer = new ResizeObserver(setWidth);
        observer.observe(box);
        return () => observer.disconnect();
    }, []);
    const handleScroll = () => {
        if (jumpLockRef.current) {
            // Programmatic scroll: release once it has been still for 150ms; keep the clicked status.
            clearTimeout(releaseTimerRef.current);
            releaseTimerRef.current = setTimeout(() => { jumpLockRef.current = null; }, 150);
            return;
        }
        cancelAnimationFrame(scrollFrameRef.current);
        scrollFrameRef.current = requestAnimationFrame(updateRailFromScroll);
    };
    // A real user scroll (wheel/touch/keys) ends any lock straight away.
    const releaseJumpLock = () => {
        pinnedRef.current = null;
        if (!jumpLockRef.current) return;
        jumpLockRef.current = null;
        clearTimeout(releaseTimerRef.current);
    };
    React.useEffect(() => () => {
        clearTimeout(releaseTimerRef.current);
        cancelAnimationFrame(scrollFrameRef.current);
    }, []);
    const jumpTo = (statusId) => {
        if (collapsedSet.has(statusId)) toggleGroup(statusId);
        setRailActive(statusId);
        jumpLockRef.current = statusId;
        pinnedRef.current = statusId;
        // After an expand renders, put the group header just under the rail.
        requestAnimationFrame(() => {
            const box = scrollRef.current;
            const el = sectionEls.current[statusId];
            if (!box || !el) { jumpLockRef.current = null; return; }
            const top = Math.min(Math.max(0, el.offsetTop - RAIL_HEIGHT), box.scrollHeight - box.clientHeight);
            if (Math.abs(box.scrollTop - top) < 1) { jumpLockRef.current = null; return; }
            const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
            box.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
            // Safety net if the browser fires no scroll events.
            clearTimeout(releaseTimerRef.current);
            releaseTimerRef.current = setTimeout(() => { jumpLockRef.current = null; }, 1200);
        });
    };

    // Bulk moves work within one status, so a selection lives in one group; touching another group starts over there.
    const activeRef = React.useRef(activeStatusId);
    React.useEffect(() => { activeRef.current = activeStatusId; }, [activeStatusId]);
    const selectionFor = (statusId) => {
        const activate = () => {
            if (activeRef.current === statusId) return false;
            activeRef.current = statusId;
            onActivateStatus(statusId);
            return true;
        };
        return {
            isActive: activeStatusId === statusId,
            selectedIds, selectAll,
            setSelectedIds: (next) => {
                if (activate()) setSelectAll(false);
                setSelectedIds(next);
            },
            setSelectAll: (checked) => {
                if (activate()) setSelectedIds(new Set());
                setSelectAll(checked);
            },
        };
    };

    return (
        <Box ref={scrollRef} onScroll={handleScroll} onWheel={releaseJumpLock} onTouchStart={releaseJumpLock} onKeyDown={releaseJumpLock}
            // Dragging the scrollbar is a user scroll too (a press on the list itself, past its content width).
            onMouseDown={(e) => { if (e.target === e.currentTarget && e.nativeEvent.offsetX >= e.currentTarget.clientWidth) releaseJumpLock(); }}
            sx={{
            position: 'relative', bgcolor: '#FFFFFF', border: '1px solid #EAECF0', borderRadius: '8px',
            // A little room under the page title before the list starts.
            mt: '16px',
            // Header row with pipeline/search moved into the rail, so the list takes that height back (less the margin).
            maxHeight: 'calc(100vh - 7rem - 16px)', overflow: 'auto',
        }}>
            {/* As wide as the widest table, so pinned pieces have room to stay put while scrolling sideways.
                Phones show cards (below sm), which must fit the screen instead. */}
            <Box sx={{ width: { xs: '100%', sm: 'max-content' }, minWidth: '100%' }}>
            <StatusRail statuses={statuses} counts={counts} activeId={railActive} onJump={jumpTo} onOpenFields={onOpenFields} onAddStatus={onAddStatus} canAddStatus={canAddStatus} railStart={railStart} railEnd={railEnd} railTrailing={railTrailing} />
            <Box ref={contentRef}>
            {statuses.map((status, index) => (
                <StatusGroup
                    key={status.value}
                    status={status}
                    investorId={investorId}
                    buildRequest={buildRequest}
                    collapsed={collapsedSet.has(status.value)}
                    count={counts?.[status.value]}
                    onToggle={toggleGroup}
                    formatRows={formatRows}
                    tableProps={tableProps}
                    selection={selectionFor(status.value)}
                    onInvite={onInvite}
                    leavingKeys={leavingKeys}
                    aiRunning={aiRunning}
                    columnsReady={columnsReady}
                    pageSize={pageSize}
                    onPageSizeChange={onPageSizeChange}
                    isFiltered={isFiltered}
                    entranceDelay={firstVisit ? index * 40 : null}
                    isAdded={Boolean(initialStatusIds.current && !initialStatusIds.current.has(status.value))}
                    pulseKey={pulse?.statusId === status.value ? pulse.key : undefined}
                    sectionRef={(el) => { sectionEls.current[status.value] = el; }}
                />
            ))}
            </Box>
            {onAddStatus && canAddStatus && (
                <ButtonBase
                    onClick={onAddStatus}
                    sx={{
                        ...PIN_LEFT, justifyContent: 'flex-start', gap: '6px', minHeight: 44, px: '16px',
                        borderTop: '1px solid #EAECF0', fontSize: '0.8125rem', fontWeight: 500, color: '#475467',
                        '&:hover': { bgcolor: '#F9FAFB', color: '#3538CD' },
                    }}
                >
                    <Add sx={{ fontSize: 18 }} /> New status
                </ButtonBase>
            )}
            </Box>
        </Box>
    );
};

export default GroupedDealList;
